import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, X } from 'lucide-react';

const results = [
  {
    id: 1,
    image: 'https://customer-assets.emergentagent.com/job_e048a018-520a-4eb5-9531-c76a0e3353aa/artifacts/5hh491g1_92f6d4e3-461f-45bc-8ed4-430d358d2aa1.jpg',
    title: 'Lip Blush',
    description: 'Pigmentação labial com técnica de aquarela para efeito ultra natural',
    category: 'Lábios',
  },
  {
    id: 2,
    image: 'https://customer-assets.emergentagent.com/job_e048a018-520a-4eb5-9531-c76a0e3353aa/artifacts/vyv3nvlb_f8a4a24a-7321-40c9-8331-0b94f818bd34.jpg',
    title: 'Jato de Plasma',
    description: 'Remoção de sinal com jato de plasma - resultado imediato',
    category: 'Tratamento',
  },
  {
    id: 3,
    image: 'https://customer-assets.emergentagent.com/job_e048a018-520a-4eb5-9531-c76a0e3353aa/artifacts/t4xag6kh_7753ae32-0efb-44ce-b39c-91073b3ea430.jpg',
    title: 'Massagem Modeladora',
    description: 'Técnica exclusiva "Bumbum na Nuca" - modelagem corporal avançada',
    category: 'Corporal',
  },
];

const ResultCard = ({ result, index, isInView, onImageClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      className="group relative glass-card rounded-2xl overflow-hidden cursor-pointer"
      onClick={() => onImageClick(result)}
      data-testid={`result-card-${result.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden">
        <img
          src={result.image}
          alt={result.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
        
        {/* Zoom icon */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 bg-gold/90 rounded-full flex items-center justify-center">
            <ZoomIn className="w-6 h-6 text-black" />
          </div>
        </div>

        {/* Category badge */}
        <div className="absolute top-4 left-4 bg-gold/90 text-black text-xs font-bold px-3 py-1 rounded-full">
          {result.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors">
          {result.title}
        </h3>
        <p className="text-white/60 text-sm line-clamp-2">
          {result.description}
        </p>
      </div>
    </motion.div>
  );
};

const ImageModal = ({ result, onClose }) => {
  if (!result) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
      onClick={onClose}
      data-testid="image-modal"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
          data-testid="modal-close"
        >
          <X className="w-8 h-8" />
        </button>
        
        <div className="rounded-2xl overflow-hidden">
          <img
            src={result.image}
            alt={result.title}
            className="w-full h-auto max-h-[80vh] object-contain"
          />
        </div>
        
        <div className="mt-4 text-center">
          <h3 className="font-heading text-2xl font-bold text-white mb-2">
            {result.title}
          </h3>
          <p className="text-white/60">{result.description}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Results = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section
      id="resultados"
      ref={ref}
      data-testid="results-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-gold font-medium tracking-wider uppercase text-sm mb-4">
            Galeria de Resultados
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Resultados que <span className="text-gold">Falam por Si</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Confira alguns dos trabalhos realizados por nossas alunas e pela própria Ritha Barreto.
          </p>
        </motion.div>

        {/* Results Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((result, index) => (
            <ResultCard
              key={result.id}
              result={result}
              index={index}
              isInView={isInView}
              onImageClick={setSelectedImage}
            />
          ))}
        </div>

        {/* View more hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-white/40 text-sm">
            Clique nas imagens para ampliar
          </p>
        </motion.div>
      </div>

      {/* Modal */}
      {selectedImage && (
        <ImageModal result={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
    </section>
  );
};
